import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';

const translations = {
  en: enTranslations,
  es: esTranslations,
};

const LanguageContext = createContext();

const STORAGE_KEY = 'jbrantley_language';
const PROMPT_SHOWN_KEY = 'jbrantley_lang_prompt_shown';

// Detect browser language
const detectBrowserLanguage = () => {
  if (typeof window === 'undefined') return 'en';
  
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  // Check if browser language starts with 'es' (Spanish)
  if (browserLang.toLowerCase().startsWith('es')) {
    return 'es';
  }
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [showLanguagePrompt, setShowLanguagePrompt] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState('en');
  
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (stored === 'en' || stored === 'es')) {
        return stored;
      }
    }
    return 'en';
  });

  // Check for language prompt on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const hasStoredPreference = localStorage.getItem(STORAGE_KEY);
    const promptShown = localStorage.getItem(PROMPT_SHOWN_KEY);
    
    // Only show prompt if no stored preference and prompt hasn't been shown
    if (!hasStoredPreference && !promptShown) {
      const browserLang = detectBrowserLanguage();
      if (browserLang === 'es') {
        setDetectedLanguage('es');
        setShowLanguagePrompt(true);
      }
    }
  }, []);

  const dismissLanguagePrompt = useCallback((selectedLang) => {
    setShowLanguagePrompt(false);
    localStorage.setItem(PROMPT_SHOWN_KEY, 'true');
    if (selectedLang) {
      setLanguageState(selectedLang);
      localStorage.setItem(STORAGE_KEY, selectedLang);
    }
  }, []);

  // Persist language preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    // Update document lang attribute for accessibility/SEO
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (lang === 'en' || lang === 'es') {
      setLanguageState(lang);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState((prev) => (prev === 'en' ? 'es' : 'en'));
  }, []);

  // Translation function with nested key support
  const t = useCallback((key, replacements = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return the key if not found
          }
        }
        break;
      }
    }
    
    // Return objects as-is (for complex data like pricing packages)
    if (typeof value === 'object') {
      return value;
    }
    
    if (typeof value !== 'string') {
      return key;
    }
    
    // Handle replacements like {category}
    let result = value;
    Object.entries(replacements).forEach(([placeholder, replacement]) => {
      result = result.replace(`{${placeholder}}`, replacement);
    });
    
    return result;
  }, [language]);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isSpanish: language === 'es',
    isEnglish: language === 'en',
    showLanguagePrompt,
    detectedLanguage,
    dismissLanguagePrompt,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default useLanguage;
